import { MessagingSession } from '@/components/MessagingSession';
import { Outlet } from 'react-router-dom';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { DialogProvider, useDialog } from '@gokrew/ui/context';
import { ResponsiveDialog } from '@gokrew/ui/dialog';
import { useMediaQuery } from '@gokrew/ui/hooks';
import { TourProvider } from '@gokrew/ui';
import { PageSuspenseFallback } from '@gokrew/ui/components/PageSuspenseFallback';
import { PageHeaderProvider } from '@/context/PageHeaderContext';
import { Sidebar } from '@/components/Sidebar';
import { EmployeeHeader } from '@/components/EmployeeHeader/EmployeeHeader';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { cn } from '@gokrew/ui/lib/utils';
import { usePermissions } from '@/context/PermissionContext';
import { preloadRoutes } from '@/utility/routePreloader';

export function EmployeePortalMainLayout() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const matches = useMediaQuery('(min-width: 1024px)');
  const { permissions } = usePermissions();
  const hasOpenedResetDialog = useRef(false);
  const { openDialog } = useDialog();
  const { user, isMockLogin } = useEmployeePortalAuth();
  const { t } = useTranslation(TranslationNS.Auth);

  useEffect(() => {
    if (permissions.size > 0) {
      preloadRoutes(permissions);
    }
  }, [permissions]);

  useEffect(() => {
    setIsSidebarCollapsed(!matches);
  }, [matches]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((collapsed) => !collapsed);
  }, []);

  const openResetPasswordDialog = useCallback(
    () =>
      openDialog({
        title: t('reset_password'),
        hideHeader: true,
        containerClass: 'w-[480px] max-w-[480px]',
        disableClose: true,
        content: <ResetPasswordPage dialogMode />,
      }),
    [openDialog, t]
  );

  useEffect(() => {
    if (
      !isMockLogin &&
      user?.user?.passwordChangedAt === null &&
      !hasOpenedResetDialog.current
    ) {
      hasOpenedResetDialog.current = true;
      openResetPasswordDialog();
    }
  }, [isMockLogin, user?.user?.passwordChangedAt, openResetPasswordDialog]);

  return (
    <MessagingSession>
      <TourProvider>
        <PageHeaderProvider>
          <DialogProvider>
            <ResponsiveDialog />
            <div className="flex h-screen bg-gray-50">
              {/* Sidebar */}
              <Sidebar
                isCollapsed={isSidebarCollapsed}
                onToggle={toggleSidebar}
              />

              {/* Main content */}
              <div
                className={cn(
                  'flex-1 flex flex-col overflow-x-auto',
                  // Below `lg`, the sidebar is fixed/overlaid (see Sidebar.module.css),
                  // so reserve space for its collapsed width here. At `lg`+ the
                  // sidebar is a flex sibling and already takes up its own space.
                  'md:ml-[var(--sidebar-width-collapsed)] lg:ml-0'
                )}
              >
                {/* Top Navigation */}
                <EmployeeHeader
                  onMenuClick={() => setIsSidebarCollapsed(false)}
                />
                {/* Main Content */}
                <main className="flex-1 overflow-auto flex flex-col">
                  {/* A page that wants to size itself from the viewport rather
                    than its content marks its root `data-fill-viewport`. */}
                  <div className="p-3 md:p-5 xl:p-8 flex-1 flex flex-col has-[[data-fill-viewport]]:min-h-0">
                    {/* A page whose code is still loading shows here, not
                      as the full-page loader that would hide the shell. */}
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <Outlet />
                    </Suspense>
                  </div>
                </main>
              </div>
            </div>
          </DialogProvider>
        </PageHeaderProvider>
      </TourProvider>
    </MessagingSession>
  );
}
